import { useMemo, useState } from "react";
import SpotifyWebApi from "spotify-web-api-js";
import { useTokenContext } from "../auth/UserTokenProvider";

const spotifyApi = new SpotifyWebApi();

const useUserPlaylists = () => {
  const { token } = useTokenContext();
  if (token) {
    spotifyApi.setAccessToken(token?.value);
  }
  const [playlists, setPlaylists] = useState<
    { id: string; name: string; icon: string; lastUpdated: string; total: number }[]
  >([]);
  const [loading, setLoading] = useState(true);

  const fetchPlaylists = async (): Promise<any[]> => {
    try {
      const data = await spotifyApi.getUserPlaylists();
      const formattedPlaylists = await Promise.all(
        data.items.map(async (playlist: any) => {
          const playlistDetails = await spotifyApi.getPlaylist(playlist.id);
          return {
            id: playlist.id,
            name: playlist.name,
            icon: playlist.images[0]?.url || "",
            lastUpdated: playlistDetails.snapshot_id,
            total: playlistDetails.tracks.total
          };
        })
      );
      setPlaylists(formattedPlaylists);
      return formattedPlaylists;
    } catch (error) {
      console.error("Error fetching playlists", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchLikedSongs = async (): Promise<{
    id: string;
    name: string;
    icon: string;
    total: number;
  }> => {
    try {
      const data = await spotifyApi.getMySavedTracks({ limit: 1 });
      const total = data.total;
      return {
        id: "liked-songs",
        name: "Liked Songs",
        icon: "path/to/liked-songs-icon.png", // Replace with the actual path to the icon
        total: total
      };
    } catch (error) {
      console.error("Error fetching liked songs", error);
      return {
        id: "liked-songs",
        name: "Liked Songs",
        icon: "path/to/liked-songs-icon.png", // Replace with the actual path to the icon
        total: 0
      };
    }
  };

  const toggleLikedSongs = async (trackURI: string): Promise<boolean> => {
    if (!trackURI) {
      return false;
    }
    const trackID = trackURI.split(":")[2];
    try {
      const data = await spotifyApi.containsMySavedTracks([trackID]);
      if (data[0]) {
        // await spotifyApi.removeFromMySavedTracks([trackID]);
        return false;
      } else {
        await spotifyApi.addToMySavedTracks([trackID]);
        return true;
      }
    } catch (error) {
      throw error;
    }
  };

  const toggleSongToPlaylist = async (playlistId: string, trackuri: string): Promise<boolean> => {
    try {
      const playlistTracks = await spotifyApi.getPlaylistTracks(playlistId);
      const trackExists = playlistTracks.items.some((item: any) => item.track.uri === trackuri);
      if (trackExists) {
        // await spotifyApi.removeTracksFromPlaylist(playlistId, [{ uri: trackuri }]);
        return false;
      } else {
        await spotifyApi.addTracksToPlaylist(playlistId, [`${trackuri}`]);
        return true;
      }
    } catch (error) {
      console.error("Error toggling song in playlist", error);
      throw new Error("");
    }
  };

  const memoizedFetchPlaylists = useMemo(() => fetchPlaylists, []);

  return {
    playlists,
    loading,
    toggleLikedSongs,
    toggleSongToPlaylist,
    fetchLikedSongs,
    fetchPlaylists: memoizedFetchPlaylists
  };
};

export default useUserPlaylists;
